import React, { useEffect, useState } from 'react';
import { getSpecificDevice } from 'api/devices';
import { Box, Typography, Grid, Alert, Chip, Divider, IconButton, Tooltip } from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { Card } from './ui/Card';
import { StatusBadge } from './ui/StatusBadge';
import { LoadingSkeleton } from './ui/LoadingSkeleton';
import { colors } from '../theme/colors';
import { dataTypography } from '../theme/typography';

interface DeviceData {
  id: string;
  device_family_name: string;
  adding_time: string;
  activation_time: string;
  description: string;
  device_api_key: string;
  device_activation_key: string;
  is_active?: boolean;
}

interface DeviceSummaryProps {
  device_id: string;
}

export const DeviceSummary: React.FC<DeviceSummaryProps> = ({ device_id }) => {
  const [deviceData, setDeviceData] = useState<DeviceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [copiedApiKey, setCopiedApiKey] = useState(false);
  const [copiedActivationKey, setCopiedActivationKey] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getSpecificDevice(device_id);
      setDeviceData(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching device data:', error);
      setError('Ошибка при загрузке данных устройства. Пожалуйста, попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyApiKey = async () => {
    if (deviceData?.device_api_key) {
      await navigator.clipboard.writeText(deviceData.device_api_key);
      setCopiedApiKey(true);
      setTimeout(() => setCopiedApiKey(false), 2000);
    }
  };

  const handleCopyActivationKey = async () => {
    if (deviceData?.device_activation_key) {
      await navigator.clipboard.writeText(deviceData.device_activation_key);
      setCopiedActivationKey(true);
      setTimeout(() => setCopiedActivationKey(false), 2000);
    }
  };

  useEffect(() => {
    fetchData();
  }, [device_id]);

  if (loading) {
    return <LoadingSkeleton type="card" />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!deviceData) {
    return <Alert severity="warning">Данные устройства не найдены.</Alert>;
  }

  return (
    <Card variant="gradient" hover={true}>
      <Box sx={{ p: 4 }}>
        {/* Header with Device ID and Status */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: colors.text.secondary,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.1em',
              }}
            >
              Устройство
            </Typography>
            <Typography
              sx={{
                ...dataTypography.id,
                fontSize: '1.5rem',
                color: colors.text.primary,
                mb: 1,
              }}
            >
              {deviceData.id}
            </Typography>
            <Chip
              label={deviceData.device_family_name}
              icon={<DeviceHubIcon />}
              sx={{
                backgroundColor: 'rgba(8, 131, 149, 0.1)',
                color: colors.primary.main,
                fontWeight: 500,
              }}
            />
          </Box>
          {deviceData.is_active !== undefined && (
            <StatusBadge
              status={deviceData.is_active ? 'active' : 'inactive'}
              label={deviceData.is_active ? 'Активно' : 'Неактивно'}
            />
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Device Information Grid */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: '0.875rem' }} />
                Дата добавления
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: colors.text.primary }}>
                {new Date(deviceData.adding_time).toLocaleString('ru-RU')}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: '0.875rem' }} />
                Дата активации
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: colors.text.primary }}>
                {new Date(deviceData.activation_time).toLocaleString('ru-RU')}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <InfoIcon sx={{ fontSize: '0.875rem' }} />
                Описание
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: colors.text.primary }}>
                {deviceData.description}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* API Keys Section */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              color: colors.text.primary,
              fontSize: '1rem',
              fontWeight: 600,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <KeyIcon sx={{ fontSize: '1.25rem' }} />
            Ключи доступа
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: colors.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colors.neutral[200]}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: colors.primary.light,
                    backgroundColor: colors.neutral[50],
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      mb: 0.75,
                      display: 'block',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    API Ключ
                  </Typography>
                  <Typography
                    sx={{
                      ...dataTypography.code,
                      color: colors.text.primary,
                      fontSize: '0.875rem',
                      wordBreak: 'break-all',
                      userSelect: 'all',
                      cursor: 'text',
                    }}
                  >
                    {deviceData.device_api_key}
                  </Typography>
                </Box>
                <Tooltip title={copiedApiKey ? 'Скопировано!' : 'Копировать'}>
                  <IconButton
                    onClick={handleCopyApiKey}
                    sx={{
                      color: copiedApiKey ? colors.success.main : colors.text.secondary,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: copiedApiKey ? 'rgba(32, 227, 178, 0.1)' : colors.neutral[100],
                        color: copiedApiKey ? colors.success.main : colors.primary.main,
                      },
                    }}
                  >
                    {copiedApiKey ? <CheckIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: colors.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colors.neutral[200]}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: colors.primary.light,
                    backgroundColor: colors.neutral[50],
                  },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      mb: 0.75,
                      display: 'block',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Ключ активации
                  </Typography>
                  <Typography
                    sx={{
                      ...dataTypography.code,
                      color: colors.text.primary,
                      fontSize: '0.875rem',
                      wordBreak: 'break-all',
                      userSelect: 'all',
                      cursor: 'text',
                    }}
                  >
                    {deviceData.device_activation_key}
                  </Typography>
                </Box>
                <Tooltip title={copiedActivationKey ? 'Скопировано!' : 'Копировать'}>
                  <IconButton
                    onClick={handleCopyActivationKey}
                    sx={{
                      color: copiedActivationKey ? colors.success.main : colors.text.secondary,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: copiedActivationKey ? 'rgba(32, 227, 178, 0.1)' : colors.neutral[100],
                        color: copiedActivationKey ? colors.success.main : colors.primary.main,
                      },
                    }}
                  >
                    {copiedActivationKey ? <CheckIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Card>
  );
};